import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useGuideSend } from './GuideSendContext';
import { absendeZustand, auftragsArt, fasseZusammen, type AbsendeArt, type AbsendeAuftrag, type AbsendeTeil, type AbsendeZustand } from './guide-absenden';

/**
 * DER EINE ABSENDEN-KNOPF (Phase 2 der Guide-Flaeche): Die Bausteine der geoeffneten Buehne (Frage mit Optionen, Fragenliste, freies Wort) melden hier, WAS bei ihnen im Feld
 * steht und WIE es eingereiht wird. Der Knopf (`AbsendenKnopf`) und jedes Enter in einem der Felder rufen dieselbe `sendeAlles`: sie reiht je Teil einen eigenen Auftrag in die
 * durable Sendewarteschlange (dieselben Aufrufe wie die alten Einzelknoepfe, nur zusammen) — nie einen Teil, zu dem schon ein unquittierter Auftrag existiert.
 */
export interface AbsendeAnbieter { art: AbsendeArt; anzahl: number; beschreibung?: string; /** Reiht den Auftrag ein; wirft bei einem Fehler (die Warteschlange meldet ihn zugleich laut). */ einreihen: () => void }
interface Wert { zustand: AbsendeZustand; sendeAlles: () => void; auftraege: AbsendeAuftrag[]; erneut: (id: string) => void; zurueckInEntwurf: (id: string) => void }
const Kontext = createContext<(Wert & { melde: (key: string, a: AbsendeAnbieter | null) => void }) | null>(null);

const signatur = (m: Map<string, AbsendeAnbieter>) => [...m.entries()].map(([k, a]) => `${k}|${a.art}|${a.anzahl}|${a.beschreibung ?? ''}`).sort().join('§');

export function AbsendeRegister({ buehneId, nachSenden, children }: { buehneId: string; /** Was nach dem Einreihen geschieht (Nachladen bzw. im Assistent-Modus das Warten auf die naechste). */ nachSenden: (art: "antwort" | "nachricht") => void; children: ReactNode }) {
  const { jobs, queue } = useGuideSend();
  const anbieter = useRef(new Map<string, AbsendeAnbieter>());
  const sig = useRef('');
  const [, setVersion] = useState(0);
  /* Ein zweites Fenster derselben Buehne aendert den gemeinsamen Speicher (Auftraege, Entwuerfe): neu zeichnen, damit der Knopf dessen laufende Auftraege sieht. */
  const [fremdTick, setFremdTick] = useState(0);
  useEffect(() => {
    const h = (ev: StorageEvent) => { if (ev.key === null || ev.key.startsWith('guide-send-v1:') || ev.key.startsWith('guide-entwurf:')) setFremdTick(n => n + 1); };
    window.addEventListener('storage', h);
    return () => window.removeEventListener('storage', h);
  }, []);
  const melde = useCallback((key: string, a: AbsendeAnbieter | null) => {
    if (a) anbieter.current.set(key, a); else anbieter.current.delete(key);
    const s = signatur(anbieter.current);
    if (s !== sig.current) { sig.current = s; setVersion(v => v + 1); }
  }, []);
  const auftraege: AbsendeAuftrag[] = useMemo(() => jobs.filter(j => j.stage.id === buehneId && !j.close && !j.raum)
    .map(j => ({ id: j.id, art: auftragsArt(j), state: j.state ?? 'queued', rejected: j.rejected, serverAccepted: j.serverAccepted, error: j.error })), [jobs, buehneId]);
  /* Auftraege eines ANDEREN Fensters (nur lesend): solange sie laufen, ist der Knopf gesperrt — sonst wuerde derselbe Text ein zweites Mal eingereiht. */
  const fremde: AbsendeAuftrag[] = useMemo(() => (queue?.fremdeAuftraege() ?? []).filter(j => j.stage.id === buehneId && !j.close && !j.raum)
    .map(j => ({ id: j.id, art: auftragsArt(j), state: 'queued' })), [queue, buehneId, jobs, fremdTick]);
  const alle = [...anbieter.current.values()];
  const teile: AbsendeTeil[] = alle.map(a => ({ art: a.art, anzahl: a.anzahl, beschreibung: a.beschreibung }));
  const zustand = absendeZustand(teile, [...auftraege, ...fremde]);
  const sendeAlles = () => {
    if (!zustand.klickbar) return;
    if (zustand.art === 'teilfehler') { for (const j of zustand.fehler) queue?.retry(j.id); return; }
    const arten = new Set(zustand.offen.map(t => t.art));
    // Antworten zuerst, dann die Vorschlags-Antworten, dann die Nachricht: alle stehen gesichert in der Schlange, bevor das Netz gefragt wird.
    for (const art of ['antwort', 'vorschlag', 'nachricht'] as const) {
      if (!arten.has(art)) continue;
      for (const a of anbieter.current.values()) if (a.art === art && a.anzahl > 0) a.einreihen();   // wirft `einreihen`, ist der Teil davor schon eingereiht: der Knopf bietet dann nur den fehlenden an
    }
    nachSenden(arten.has('antwort') || arten.has('vorschlag') ? 'antwort' : 'nachricht');
  };
  const wert = { zustand, sendeAlles, auftraege, melde, erneut: (id: string) => queue?.retry(id), zurueckInEntwurf: (id: string) => queue?.returnToDraft(id) };
  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

/** Ein Baustein meldet sein Feld. Ohne Register (z. B. in einem Einzeltest) passiert nichts. */
export function useAbsendeAnbieter(key: string, a: AbsendeAnbieter): void {
  const k = useContext(Kontext);
  useEffect(() => { k?.melde(key, a); });
  useEffect(() => () => { k?.melde(key, null); }, [key, k?.melde]);   // eslint-disable-line react-hooks/exhaustive-deps
}
/** Fuer Enter in den Feldern und fuer den Knopf: dieselbe Aktion. */
export function useAbsenden(): Wert | null { return useContext(Kontext); }
export { fasseZusammen };
